import React from "react";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import {
  LegalMobileDropdown,
  LegalDesktopSidebar,
  useActiveSection,
  scrollToSection
} from "../../components/legal/LegalNav";

const PrivacyPolicy = () => {
  const sections = [
    { id: "intro", title: "1. Introduction" },
    { id: "controller", title: "2. Data Controller" },
    { id: "collection", title: "3. Data Collection" },
    { id: "use", title: "4. Data Use" },
    { id: "sharing", title: "5. Data Sharing" },
    { id: "security", title: "6. Data Security" },
    { id: "rights", title: "7. User Rights" },
    { id: "retention", title: "8. Data Retention" },
    { id: "cookies", title: "9. Cookies & Tracking" },
    { id: "children", title: "10. Children’s Privacy" },
    { id: "scenarios", title: "11. Specific Scenarios" },
    { id: "compliance", title: "12. Legal Compliance" },
    { id: "changes", title: "13. Notification of Changes" },
    { id: "contact", title: "14. Contact Information" },
  ];

  const [activeSection, setActiveSection] = useActiveSection(sections);

  const handleSelectSection = (targetId) => {
    setActiveSection(targetId);
    scrollToSection(targetId, window.innerWidth < 768);
  };

  return (
    <div className="landing-page-body" style={{ backgroundColor: "#F9FAFB" }}>
      <div className="header-bg">
        <Header />
        {/* Hero Section */}
        <div style={{ padding: "60px 0 80px 0", color: "#white", textAlign: "center" }}>
          <div className="container text-white">
            <span className="br-16 poppins f-12 fw-semibold mb-3" style={{ backgroundColor: "rgba(255, 255, 255, 0.15)", color: "#ffffff", border: "1px solid rgba(255, 255, 255, 0.25)", textTransform: "uppercase", letterSpacing: "1px", padding: "8px 20px", display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: "1", verticalAlign: "middle" }}>Next Salon Legal</span>
            <h1 className="poly fw-bold text-white mb-3" style={{ fontSize: "2.8rem" }}>Privacy Policy</h1>
            <p className="poppins text-white-50" style={{ maxWidth: "600px", margin: "0 auto", fontSize: "16px" }}>
              Learn how we collect, use, and safeguard your personal data. Last updated July 2026.
            </p>
          </div>
        </div>
      </div>

      <div className="container py-5">
        <div className="row">
          {/* Sidebar Navigation (Desktop / Tablet) */}
          <div className="col-md-4 col-lg-3 d-none d-md-block">
            <LegalDesktopSidebar
              sections={sections}
              activeSection={activeSection}
              onSelectSection={handleSelectSection}
            />
          </div>

          {/* Policy Content */}
          <div className="col-md-8 col-lg-9 col-12">
            {/* Collapsible Section Dropdown (Mobile) */}
            <LegalMobileDropdown
              sections={sections}
              activeSection={activeSection}
              onSelectSection={handleSelectSection}
            />

            <div className="bg-white p-4 p-md-5 br-16 shadow-sm border poppins color-primary" style={{ lineHeight: "1.8", color: "#374151" }}>
              
              <div id="intro" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">1. Introduction</h3>
                <p>At Next Salon, we are committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data.</p>
              </div>

              <hr className="my-4" />

              <div id="controller" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">2. Data Controller</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li>
                    <span className="fw-semibold">Data Controller: </span>
                    Next Salon Ltd. is your Data Controller and responsible for your personal data. For any inquiries, please contact us at <a href="mailto:support@nextsalon.com" className="color-primary fw-semibold text-decoration-none" style={{ borderBottom: "1px dashed #2b5c35" }}>support@nextsalon.com</a>.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="collection" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">3. Data Collection</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Types of Data: </span>
                    Personal data (name, email, phone number), booking details, payment information, and social media profile information (if linked).
                  </li>
                  <li>
                    <span className="fw-semibold">Collection Methods: </span>
                    Data is collected through account registration, booking forms, cookies, and third-party integrations (e.g., social media logins).
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="use" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">4. Data Use</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Usage Purposes: </span>
                    Data is used to facilitate bookings, process payments, improve user experience, send marketing communications (with consent), and comply with legal obligations.
                  </li>
                  <li>
                    <span className="fw-semibold">Marketing: </span>
                    Users can opt-in to receive newsletters and promotional materials. Data from marketing interactions is used to tailor future communications.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="sharing" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">5. Data Sharing</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Third-Party Sharing: </span>
                    Data is shared with payment processors, email service providers, and social media platforms (for login and marketing). All third parties are required to comply with data protection standards.
                  </li>
                  <li>
                    <span className="fw-semibold">International Transfer: </span>
                    Data may be transferred to countries outside the user’s jurisdiction, but always with adequate safeguards in place.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="security" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">6. Data Security</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Protection Measures: </span>
                    Data is encrypted during transmission and storage. Regular security audits are conducted, and access to data is restricted to authorized personnel.
                  </li>
                  <li>
                    <span className="fw-semibold">Data Breaches: </span>
                    Users are notified within 72 hours of any data breach that may affect their personal data. Next Salon has a detailed data breach response plan, including notifying affected users and providing support such as credit monitoring services.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="rights" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">7. User Rights</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Rights: </span>
                    Users can access, correct, delete their data, and object to data processing. They can also request data portability and restrict processing in certain circumstances.
                  </li>
                  <li>
                    <span className="fw-semibold">Exercising Rights: </span>
                    Users can contact Next Salon’s support team via email to exercise their rights.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="retention" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">8. Data Retention</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Retention Period: </span>
                    Personal data is retained for as long as necessary to provide services and fulfill legal obligations. Data from inactive accounts is deleted after two years.
                  </li>
                  <li>
                    <span className="fw-semibold">Deletion Requests: </span>
                    Users can request deletion of their data, which Next Salon will process within a reasonable timeframe, unless retention is required by law.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="cookies" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">9. Cookies and Tracking</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Usage: </span>
                    Next Salon uses cookies to enhance user experience, analyze site usage, and deliver personalized ads. Users are informed about cookies upon their first visit and can manage their preferences.
                  </li>
                  <li>
                    <span className="fw-semibold">Informing Users: </span>
                    A detailed cookie policy is available on the website, explaining the types of cookies used and their purposes.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="children" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">10. Children’s Privacy</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li className="mb-2">
                    <span className="fw-semibold">Data Collection from Minors: </span>
                    Next Salon does not knowingly collect data from children under 16. If discovered, such data is deleted immediately.
                  </li>
                  <li>
                    <span className="fw-semibold">Protection Measures: </span>
                    Parental consent is required for users under 18. Next Salon will implement an age verification process during account creation.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="scenarios" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">11. Specific Scenarios</h3>
                <ul className="ps-3" style={{ listStyleType: "decimal" }}>
                  <li className="mb-3">
                    <span className="fw-semibold">Service Provider Promotions: </span>
                    <ul className="ps-3 mt-2" style={{ listStyleType: "circle" }}>
                      <li>
                        <span className="fw-semibold">Terms: </span>
                        Providers can run promotions but must adhere to Next Salon’s guidelines to ensure transparency and fairness. All promotions must be clearly communicated and honor all bookings made during the promotion period.
                      </li>
                    </ul>
                  </li>
                  <li className="mb-3">
                    <span className="fw-semibold">Dispute Resolution: </span>
                    <ul className="ps-3 mt-2" style={{ listStyleType: "circle" }}>
                      <li>
                        <span className="fw-semibold">Process: </span>
                        Users can report issues via the platform’s support system. Next Salon facilitates communication between the user and provider but does not guarantee resolution. A comprehensive dispute resolution process, including mediation, will be in place.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <span className="fw-semibold">Cross-Promotion and Partnerships: </span>
                    <ul className="ps-3 mt-2" style={{ listStyleType: "circle" }}>
                      <li className="mb-2">
                        <span className="fw-semibold">Terms: </span>
                        Next Salon engages in cross-promotion with other brands and influencers under strict agreements to protect user data. Users are informed of such partnerships and can opt-out.
                      </li>
                      <li>
                        <span className="fw-semibold">Data Protection: </span>
                        All partners are required to adhere to Next Salon’s data protection standards.
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="compliance" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">12. Legal Compliance and User Communication</h3>
                <ul className="ps-3" style={{ listStyleType: "decimal" }}>
                  <li className="mb-3">
                    <span className="fw-semibold">Legal Compliance: </span>
                    <ul className="ps-3 mt-2" style={{ listStyleType: "circle" }}>
                      <li className="mb-2">
                        <span className="fw-semibold">Regulations: </span>
                        Next Salon complies with GDPR, CCPA, and other relevant data protection regulations. A Data Protection Officer (DPO) and legal team are appointed to ensure compliance with regulations.
                      </li>
                      <li>
                        <span className="fw-semibold">Policy Updates: </span>
                        Policies will be regularly reviewed and updated to reflect changes in the law.
                      </li>
                    </ul>
                  </li>
                  <li>
                    <span className="fw-semibold">User Communication: </span>
                    <ul className="ps-3 mt-2" style={{ listStyleType: "circle" }}>
                      <li className="mb-2">
                        <span className="fw-semibold">Communication Channels: </span>
                        Next Salon uses email, in-app notifications, and the website to communicate with users about their data and privacy rights.
                      </li>
                      <li>
                        <span className="fw-semibold">Opt-In/Opt-Out: </span>
                        Users can manage their communication preferences through their account settings.
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="changes" className="mb-5 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">13. Notification of Changes and Acceptance of Policy</h3>
                <ul className="ps-3" style={{ listStyleType: "disc" }}>
                  <li>
                    <span className="fw-semibold">Updates: </span>
                    Next Salon will update this Privacy Policy periodically. Users will be notified of significant changes via email and notices on the platform. Continued use of Next Salon implies acceptance of the updated policy.
                  </li>
                </ul>
              </div>

              <hr className="my-4" />

              <div id="contact" className="mb-0 pt-3">
                <h3 className="poly fw-bold color-primary mb-3">14. Contact Information</h3>
                <p>
                  For any questions or concerns about these Terms and Conditions or Privacy Policy, please contact us at{" "}
                  <a href="mailto:support@nextsalon.com" className="color-primary fw-semibold text-decoration-none" style={{ borderBottom: "1px dashed #2b5c35" }}>
                    support@nextsalon.com
                  </a>
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
